import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { handleSupabaseError } from '@/lib/supabaseClient';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  producerId: string;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => Promise<void>;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  clearCart: () => void;
  total: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: async (item) => {
        try {
          // First, check stock level in database
          const { data, error } = await supabase
            .from('products')
            .select('stock_level')
            .eq('id', item.id)
            .single();
          
          if (error) {
            handleSupabaseError(error, 'cartStore:addItem');
            // If we can't check stock, just add the item without a check
            addItemToCart(item, get, set);
            return;
          }
          
          // Don't add if out of stock
          if (!data || data.stock_level <= 0) {
            if (import.meta.env.DEV) {
              console.warn('Product is out of stock:', item.id);
            }
            return;
          }
          
          const items = get().items;
          const existingItem = items.find((i) => i.id === item.id);
          
          // If adding more would exceed stock, limit to available stock
          if (existingItem && (existingItem.quantity + 1) > data.stock_level) {
            if (import.meta.env.DEV) {
              console.warn('Cannot add more items - would exceed stock level');
            }
            return;
          }
          
          addItemToCart(item, get, set);
        } catch (error) {
          handleSupabaseError(error, 'cartStore:addItem');
          // If connection fails, add the item anyway
          addItemToCart(item, get, set);
        }
      },
      removeItem: (id) => {
        set({ items: get().items.filter((i) => i.id !== id) });
      },
      updateQuantity: async (id, quantity) => {
        if (quantity < 1) {
          get().removeItem(id);
          return;
        }

        try {
          // Check stock level in database
          const { data, error } = await supabase
            .from('products')
            .select('stock_level')
            .eq('id', id)
            .single();
          
          if (error) {
            handleSupabaseError(error, 'cartStore:updateQuantity');
            return;
          }
          
          // Don't update if it would exceed stock
          if (!data || quantity > data.stock_level) {
            if (import.meta.env.DEV) {
              console.warn('Cannot update quantity - would exceed stock level');
            }
            return;
          }
          
          set({
            items: get().items.map((i) =>
              i.id === id ? { ...i, quantity } : i
            ),
          });
        } catch (error) {
          handleSupabaseError(error, 'cartStore:updateQuantity');
        }
      },
      clearCart: () => set({ items: [] }),
      total: () => {
        try {
          return get().items.reduce(
            (total, item) => {
              // Ensure price is a valid number and positive
              const price = typeof item.price === 'number' ? 
                Math.max(0, item.price) : 
                parseFloat(String(item.price)) || 0;
              
              // Ensure quantity is a valid number and positive
              const quantity = typeof item.quantity === 'number' ? 
                Math.max(0, item.quantity) : 
                parseInt(String(item.quantity)) || 0;
              
              // Log any invalid values in development
              if (import.meta.env.DEV && (isNaN(price) || price <= 0)) {
                console.warn(`Invalid price for item ${item.id}:`, item.price);
              }
              if (import.meta.env.DEV && (isNaN(quantity) || quantity <= 0)) {
                console.warn(`Invalid quantity for item ${item.id}:`, item.quantity);
              }
              
              return total + (price * quantity);
            },
            0
          );
        } catch (error) {
          console.error('Error calculating cart total:', error);
          return 0;
        }
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);

// Helper function to add item to cart
function addItemToCart(
  item: Omit<CartItem, 'quantity'>, 
  get: () => CartStore, 
  set: (partial: Partial<CartStore> | ((state: CartStore) => Partial<CartStore>)) => void
) {
  const items = get().items;
  const existingItem = items.find((i) => i.id === item.id);
  
  if (existingItem) {
    set({
      items: items.map((i) =>
        i.id === item.id
          ? { ...i, quantity: i.quantity + 1 }
          : i
      ),
    });
  } else {
    set({ items: [...items, { ...item, quantity: 1 }] });
  }
}